/**
 * Contractor dashboard preset.
 *
 * Built entirely on the existing dashboard framework, so drill-down, breakdown,
 * RBAC-per-metric and PDF/Excel/CSV/PowerPoint export all come for free — this
 * file is configuration, not a second dashboard engine.
 *
 * ACTION-ORIENTED, not a report. The first thing a reader sees is what needs
 * their attention today, and every number opens the records behind it. A
 * contractor dashboard that only counts contractors tells an EHS lead nothing
 * they can act on.
 *
 * Note which figures are ORGANISATION-WIDE: contractor_companies has no site
 * column, so firm-level counts do not narrow with the site filter. Worker
 * figures do. The KPI titles say so rather than leaving a reader to assume.
 */

/** Needs attention today — the reason to open this page. */
export const ATTENTION_METRICS = [
  'contractor.pending_approval',
  'contractor.awaiting_induction',
  'contractor.induction_expired',
  'contractor.induction_expiring',
  'contractor.medical_expiring',
  'contractor.gate_pass_expired',
  'contractor.docs_expiring',
  'contractor.suspended',
];

/** The population being managed. */
export const POPULATION_METRICS = [
  'contractor.companies',
  'contractor.workers',
  'contractor.induction_done',
  'contractor.induction_rate',
];

/**
 * Derived performance — counted from the EHS records now attributable to a
 * contractor (phase 4 FKs), so nobody maintains a safety score by hand.
 */
export const PERFORMANCE_METRICS = [
  'contractor.incidents',
  'contractor.observations',
];

export function buildContractorCatalog() {
  return [
    {
      id: 'kpis-attention', type: 'kpi-group', colSpan: 2,
      title: 'Needs attention',
      props: { metrics: ATTENTION_METRICS, columns: 4 },
    },
    {
      id: 'kpis-population', type: 'kpi-group', colSpan: 2,
      title: 'Contractor population',
      props: { metrics: POPULATION_METRICS, columns: 4 },
    },
    {
      id: 'kpis-performance', type: 'kpi-group', colSpan: 2,
      title: 'Contractor EHS performance',
      props: { metrics: PERFORMANCE_METRICS, columns: 4 },
    },
    // Which contractors carry the workforce, and which carry the problems.
    {
      id: 'workers-by-contractor', type: 'breakdown', colSpan: 1,
      title: 'Workers by Contractor',
      props: {
        title: 'Active Workers by Contractor',
        metric: 'contractor.workers', groupBy: 'contractor',
        chartType: 'hbar', showValues: true, height: 300,
      },
    },
    {
      id: 'awaiting-induction-by-contractor', type: 'breakdown', colSpan: 1,
      title: 'Awaiting Induction by Contractor',
      props: {
        title: 'Workers Awaiting Induction by Contractor',
        metric: 'contractor.awaiting_induction', groupBy: 'contractor',
        chartType: 'hbar', showValues: true, height: 300,
      },
    },
    {
      id: 'workers-by-trade', type: 'breakdown', colSpan: 1,
      title: 'Workers by Trade',
      props: {
        title: 'Active Workers by Trade',
        metric: 'contractor.workers', groupBy: 'trade',
        chartType: 'donut', showValues: true, height: 300,
      },
    },
    {
      id: 'incidents-by-contractor', type: 'breakdown', colSpan: 1,
      title: 'Incidents by Contractor',
      props: {
        title: 'Incidents Attributed by Contractor',
        metric: 'contractor.incidents', groupBy: 'contractor',
        chartType: 'hbar', showValues: true, height: 300,
      },
    },
  ];
}

export function contractorDefaultLayout() {
  const spans = {
    'kpis-attention': 2,
    'kpis-population': 2,
    'kpis-performance': 2,
    'workers-by-contractor': 1,
    'awaiting-induction-by-contractor': 1,
    'workers-by-trade': 1,
    'incidents-by-contractor': 1,
  };
  return [
    'kpis-attention',
    'kpis-population',
    'workers-by-contractor',
    'awaiting-induction-by-contractor',
    'kpis-performance',
    'incidents-by-contractor',
    'workers-by-trade',
  ].map((id) => ({ id, colSpan: spans[id] || 1 }));
}

/** Export spec — same contract as the safety presets. */
export function contractorExportSpec(visibleIds) {
  const set = new Set(Array.isArray(visibleIds) ? visibleIds : []);
  const spec = {};
  const sections = [];
  if (set.has('kpis-attention')) {
    sections.push({ label: 'Needs Attention', accent: 'amber',
      metrics: ATTENTION_METRICS });
  }
  if (set.has('kpis-population')) {
    sections.push({ label: 'Contractor Population', accent: 'blue',
      metrics: POPULATION_METRICS });
  }
  if (set.has('kpis-performance')) {
    sections.push({ label: 'Contractor EHS Performance', accent: 'indigo',
      metrics: PERFORMANCE_METRICS });
  }
  if (sections.length) {
    spec.sections = sections;
    spec.metrics = sections.flatMap((s) => s.metrics);
  }

  const breakdowns = [];
  if (set.has('workers-by-contractor')) {
    breakdowns.push({ title: 'Active Workers by Contractor',
      metric: 'contractor.workers', group_by: 'contractor', chart_type: 'hbar' });
  }
  if (set.has('awaiting-induction-by-contractor')) {
    breakdowns.push({ title: 'Workers Awaiting Induction by Contractor',
      metric: 'contractor.awaiting_induction', group_by: 'contractor',
      chart_type: 'hbar' });
  }
  if (set.has('workers-by-trade')) {
    breakdowns.push({ title: 'Active Workers by Trade',
      metric: 'contractor.workers', group_by: 'trade', chart_type: 'donut' });
  }
  if (set.has('incidents-by-contractor')) {
    breakdowns.push({ title: 'Incidents Attributed by Contractor',
      metric: 'contractor.incidents', group_by: 'contractor', chart_type: 'hbar' });
  }
  if (breakdowns.length) spec.breakdowns = breakdowns;

  return spec;
}
